import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Edit3, Eye, FileText, Loader2, Plus, Search, Send, Trash2, Users, X } from "lucide-react";
import toast from "react-hot-toast";
import { GetWhatsappDbTemplates, GetWhatsappMetaTemplates, GetWhatsappTemplateFieldSources, GetWhatsappTemplateRecipients, GetWhatsappTemplates, SearchUsers } from "../apis/get";
import { DeleteWhatsappMetaTemplate } from "../apis/delete";
import {
    PatchWhatsappMetaTemplate,
    PostCreateWhatsappTemplate,
    PostSendWhatsappDbTemplateBulk,
    PostSendWhatsappTemplateBulk,
    PostSyncWhatsappDbTemplatesFromMeta,
    PostUploadWhatsappTemplateHeaderImage,
    PostWhatsappTemplateHeaderSample,
    PostWhatsappRecipientCount,
} from "../apis/post";
import { useWhatsapp, WhatsappTemplateSchema } from "./WhatsappContext";
import { buildTemplatePreview } from "./templatePreview";
import { isAutomaticTemplate } from "../../utilities/whatsappTemplatesConfig";

type TriggerFilter = "all" | "automatic" | "manual";
type SourceFilter = "all" | "db" | "code";
type RecipientMode = "phone" | "search" | "all";
type GenderFilter = "all" | "male" | "female";
type PremiumFilter = "all" | "premium" | "non_premium";
type CommunityFilter = "all" | "maheshwari" | "non_maheshwari";
type UserStatusFilter = "active" | "inactive" | "hold" | "all";
type PhotoFilter = "all" | "has_photo" | "missing_photo";
type TemplateLanguage = "en" | "hi";
type HeaderType = "NONE" | "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";
type TemplateButtonType = "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
type TemplateValueMode = "manual" | "field";
type TemplateConditionOperator = "not_equal" | "equal";
type RecipientCustomFilterOperator =
    | "equals"
    | "not_equals"
    | "contains"
    | "not_contains"
    | "starts_with"
    | "ends_with"
    | "is_empty"
    | "is_not_empty"
    | "greater_than"
    | "less_than"
    | "greater_equal"
    | "less_equal"
    | "between"
    | "in"
    | "not_in"
    | "equals_column"
    | "not_equals_column";
type RecipientCustomFilterMatch = "all" | "any";

interface TemplateValueMapping {
    mode: TemplateValueMode;
    source: string;
    table: string;
    value: string;
    default: string;
}

interface TemplateSendCondition {
    left: string;
    operator: TemplateConditionOperator;
    right: string;
}

interface RecipientCustomFilter {
    id: string;
    table: string;
    source: string;
    operator: RecipientCustomFilterOperator;
    value: string;
    valueTo: string;
    rightTable: string;
    rightSource: string;
}

interface TemplateSendResult {
    user_id?: string;
    name?: string;
    phone?: string;
    success?: boolean;
    skipped?: boolean;
    status_code?: number;
    error?: string;
    response?: any;
}

interface CreateTemplateButton {
    type: TemplateButtonType;
    text: string;
    url: string;
    phone_number: string;
}

interface CreateTemplateForm {
    name: string;
    category: string;
    language: string;
    body: string;
    bodyExamples: string;
    headerType: HeaderType;
    headerText: string;
    headerSample: string;
    headerFileName: string;
    footerText: string;
    buttons: CreateTemplateButton[];
}

interface SearchUserRow {
    id: string;
    name: string;
    phone?: string | null;
    status?: string;
    location?: string;
}

interface TemplateFieldSource {
    value: string;
    label: string;
    table?: string;
    column?: string;
    data_type?: string;
}

const defaultTemplateFieldSources: TemplateFieldSource[] = [
    { value: "user.name", label: "User/Profile Name" },
    { value: "user.phone", label: "Phone Number" },
    { value: "user.email", label: "Email" },
    { value: "profile.gender", label: "Gender" },
    { value: "profile.city", label: "City" },
    { value: "profile.state", label: "State" },
    { value: "profile.caste", label: "Caste" },
    { value: "profile.sub_caste", label: "Sub Caste" },
    { value: "user.community", label: "Community" },
    { value: "profile.manglik", label: "Profile Manglik Status" },
    { value: "profile.is_manglik", label: "Is Manglik" },
    { value: "profile.ai_manglik_review", label: "AI Manglik Review" },
    { value: "profile.annual_income_inr", label: "Annual Income" },
    { value: "profile.family_income_inr", label: "Family Income" },
    { value: "profile.occupation", label: "Occupation" },
    { value: "profile.qualification", label: "Qualification" },
    { value: "profile.birth_place", label: "Birth Place" },
    { value: "profile.date_of_birth", label: "Date of Birth" },
    { value: "profile.marital_status", label: "Marital Status" },
    { value: "profile.gotra", label: "Gotra" },
    { value: "profile.native_place", label: "Native Place" },
];

interface MetaTemplateRow {
    id?: string;
    name: string;
    status?: string;
    category?: string;
    language?: string;
    rejected_reason?: string;
    quality_score?: {
        score?: string;
        date?: number;
    };
    components?: Array<Record<string, any>>;
}

interface EditMetaTemplateForm {
    category: string;
    body: string;
    bodyExamples: string;
    headerText: string;
    footerText: string;
    buttonText: string;
    buttonUrl: string;
}

const isTemplateAutomatic = (template: WhatsappTemplateSchema, templateKey?: string) =>
    template.trigger_type === "automatic" ||
    template.is_automatic === true ||
    isAutomaticTemplate(templateKey || "");

const isDbBackedTemplate = (template?: WhatsappTemplateSchema) =>
    Boolean(template?.id || template?.source === "meta" || template?.source === "manual");

const normalizeDbTemplate = (template: any): WhatsappTemplateSchema | null => {
    const key = template?.template_key || template?.key || template?.template_name;
    const templateName = template?.template_name || template?.name || key;
    if (!key || !templateName) return null;
    const components = template?.components_json || template?.components || [];
    const parameters = [...(template?.parameters || template?.parameters_json || [])];
    const header = Array.isArray(components)
        ? components.find((component: any) => String(component?.type || "").toUpperCase() === "HEADER")
        : null;
    const headerFormat = String(header?.format || "").toUpperCase();
    if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)) {
        const mediaType = headerFormat.toLowerCase();
        const mediaKey = `header_${mediaType}_url`;
        if (!parameters.some((parameter: any) => parameter?.key === mediaKey)) {
            parameters.unshift({
                key: mediaKey,
                label: `Header ${headerFormat.charAt(0)}${headerFormat.slice(1).toLowerCase()} URL`,
                required: true,
                component: "header",
                position: 0,
            });
        }
        if (headerFormat === "DOCUMENT" && !parameters.some((parameter: any) => parameter?.key === "header_document_filename")) {
            parameters.unshift({
                key: "header_document_filename",
                label: "Header Document Filename",
                required: false,
                component: "header",
                position: 0,
                default: "document.pdf",
            });
        }
    }

    return {
        ...template,
        key,
        template_key: key,
        template_name: templateName,
        language_code: template?.language_code || template?.language || "en",
        body: template?.body || template?.message_template || template?.message || "",
        message_template: template?.message_template || template?.body || "",
        full_message: template?.full_message || template?.message || template?.body || "",
        components_json: components,
        parameters,
    };
};

const mergeTemplateRecord = (
    base: WhatsappTemplateSchema | undefined,
    incoming: WhatsappTemplateSchema,
): WhatsappTemplateSchema => {
    const baseParamsByKey = new Map((base?.parameters || []).map((parameter) => [parameter.key, parameter]));
    const incomingParams = incoming.parameters || [];
    const mergedParameters = incomingParams.length
        ? incomingParams.map((parameter) => ({
            ...(baseParamsByKey.get(parameter.key) || {}),
            ...parameter,
            default: parameter.default || baseParamsByKey.get(parameter.key)?.default,
        }))
        : base?.parameters || [];

    return {
        ...(base || {}),
        ...incoming,
        body: incoming.body || base?.body || "",
        message_template: incoming.message_template || base?.message_template || incoming.body || base?.body || "",
        message: incoming.message || base?.message || incoming.full_message || base?.full_message || "",
        full_message: incoming.full_message || base?.full_message || incoming.message || base?.message || "",
        parameters: mergedParameters,
    };
};

const mergeTemplateMaps = (
    base: Record<string, WhatsappTemplateSchema>,
    incoming: Record<string, WhatsappTemplateSchema>,
) =>
    Object.entries(incoming).reduce((acc, [key, template]) => {
        acc[key] = mergeTemplateRecord(acc[key], template);
        return acc;
    }, { ...base });

const normalizeTemplateCatalog = (catalog: any): Record<string, WhatsappTemplateSchema> => {
    if (!catalog) return {};

    if (Array.isArray(catalog)) {
        return catalog.reduce((acc: Record<string, WhatsappTemplateSchema>, item: any) => {
            const template = normalizeDbTemplate(item);
            const key = template?.template_key || template?.key || template?.template_name;
            if (template && key) {
                acc[key] = template;
            }
            return acc;
        }, {});
    }

    if (typeof catalog === "object") {
        return Object.entries(catalog).reduce((acc: Record<string, WhatsappTemplateSchema>, [catalogKey, item]: [string, any]) => {
            const template = normalizeDbTemplate(item);
            if (template) {
                acc[catalogKey] = {
                    ...template,
                    key: template.key || item?.key || catalogKey,
                    template_key: template.template_key || catalogKey,
                };
            }
            return acc;
        }, {});
    }

    return {};
};

const initialCreateTemplateForm: CreateTemplateForm = {
    name: "",
    category: "UTILITY",
    language: "en",
    body: "",
    bodyExamples: "",
    headerType: "NONE",
    headerText: "",
    headerSample: "",
    headerFileName: "",
    footerText: "",
    buttons: [],
};

const getBodyPlaceholderCount = (body: string) => {
    const matches = body.match(/{{\s*\d+\s*}}/g) || [];
    return new Set(matches.map((match) => match.replace(/\D/g, ""))).size;
};

const getBodyPlaceholderIndexes = (body: string) => {
    const matches = body.match(/{{\s*\d+\s*}}/g) || [];
    return Array.from(new Set(matches.map((match) => Number(match.replace(/\D/g, "")))))
        .filter((value) => Number.isFinite(value) && value > 0)
        .sort((a, b) => a - b);
};

const getMetaComponent = (template: MetaTemplateRow | null, type: string) =>
    template?.components?.find((component) => String(component.type || "").toUpperCase() === type) || null;

const getBodyExamples = (component: Record<string, any> | null) => {
    const bodyText = component?.example?.body_text;
    if (Array.isArray(bodyText) && Array.isArray(bodyText[0])) {
        return bodyText[0].map((value: unknown) => String(value ?? "")).join("\n");
    }
    return "";
};

const buildMetaComponents = (form: EditMetaTemplateForm) => {
    const components: Array<Record<string, any>> = [];
    if (form.headerText.trim()) {
        components.push({ type: "HEADER", format: "TEXT", text: form.headerText.trim() });
    }
    const bodyComponent: Record<string, any> = { type: "BODY", text: form.body.trim() };
    const examples = form.bodyExamples.split("\n").map((value) => value.trim()).filter(Boolean);
    if (examples.length > 0) {
        bodyComponent.example = { body_text: [examples] };
    }
    components.push(bodyComponent);
    if (form.footerText.trim()) {
        components.push({ type: "FOOTER", text: form.footerText.trim() });
    }
    if (form.buttonText.trim() && form.buttonUrl.trim()) {
        components.push({
            type: "BUTTONS",
            buttons: [{ type: "URL", text: form.buttonText.trim(), url: form.buttonUrl.trim() }],
        });
    }
    return components;
};

const recipientFilterGroups = {
    gender: [
        ["all", "All"],
        ["male", "Male"],
        ["female", "Female"],
    ] as Array<[GenderFilter, string]>,
    premium: [
        ["all", "All"],
        ["premium", "Premium"],
        ["non_premium", "Non Premium"],
    ] as Array<[PremiumFilter, string]>,
    community: [
        ["all", "All"],
        ["maheshwari", "Maheshwari"],
        ["non_maheshwari", "Non Maheshwari"],
    ] as Array<[CommunityFilter, string]>,
    status: [
        ["active", "Active"],
        ["inactive", "Inactive"],
        ["hold", "Hold"],
        ["all", "All"],
    ] as Array<[UserStatusFilter, string]>,
    photo: [
        ["all", "All"],
        ["has_photo", "Has Photo"],
        ["missing_photo", "Missing Photo"],
    ] as Array<[PhotoFilter, string]>,
};

const templateLanguageOptions = [
    ["en", "Hinglish"],
    ["hi", "Hindi"],
] as Array<[TemplateLanguage, string]>;

const recipientCustomFilterOperators = [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["contains", "Contains"],
    ["not_contains", "Does not contain"],
    ["starts_with", "Starts with"],
    ["ends_with", "Ends with"],
    ["is_empty", "Is empty"],
    ["is_not_empty", "Is not empty"],
    ["greater_than", "Greater than"],
    ["less_than", "Less than"],
    ["greater_equal", "Greater or equal"],
    ["less_equal", "Less or equal"],
    ["between", "Between"],
    ["in", "In list"],
    ["not_in", "Not in list"],
    ["equals_column", "Equals another column"],
    ["not_equals_column", "Not equals another column"],
] as Array<[RecipientCustomFilterOperator, string]>;

const operatorNeedsValue = (operator: RecipientCustomFilterOperator) =>
    !["is_empty", "is_not_empty", "equals_column", "not_equals_column"].includes(operator);

const operatorNeedsRightColumn = (operator: RecipientCustomFilterOperator) =>
    ["equals_column", "not_equals_column"].includes(operator);

const makeRecipientCustomFilter = (): RecipientCustomFilter => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    table: "",
    source: "",
    operator: "equals",
    value: "",
    valueTo: "",
    rightTable: "",
    rightSource: "",
});

const TemplateCatalogView: React.FC = () => {
    const { templates: wsTemplates, templatesCount, fetchTemplates, isConnected } = useWhatsapp();
    const [restTemplates, setRestTemplates] = useState<Record<string, WhatsappTemplateSchema>>({});
    const [dbTemplates, setDbTemplates] = useState<Record<string, WhatsappTemplateSchema>>({});
    const [search, setSearch] = useState("");
    const [triggerFilter, setTriggerFilter] = useState<TriggerFilter>("all");
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
    const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [templateParams, setTemplateParams] = useState<Record<string, string>>({});
    const [templateValueMappings, setTemplateValueMappings] = useState<Record<string, TemplateValueMapping>>({});
    const [templateSendConditions, setTemplateSendConditions] = useState<TemplateSendCondition[]>([]);
    const [templateFieldSources, setTemplateFieldSources] = useState<TemplateFieldSource[]>(defaultTemplateFieldSources);
    const [recipientMode, setRecipientMode] = useState<RecipientMode>("phone");
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<SearchUserRow[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedUsersMap, setSelectedUsersMap] = useState<Record<string, SearchUserRow>>({});
    const [searchLoading, setSearchLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [uploadingTemplateParamKey, setUploadingTemplateParamKey] = useState<string | null>(null);
    const [recipientCount, setRecipientCount] = useState<number | null>(null);
    const [recipientCountLoading, setRecipientCountLoading] = useState(false);
    const [activeOnly, setActiveOnly] = useState(true);
    const [sendLimit, setSendLimit] = useState(500);
    const [genderFilter, setGenderFilter] = useState<GenderFilter>("all");
    const [premiumFilter, setPremiumFilter] = useState<PremiumFilter>("all");
    const [communityFilter, setCommunityFilter] = useState<CommunityFilter>("all");
    const [userStatusFilter, setUserStatusFilter] = useState<UserStatusFilter>("active");
    const [photoFilter, setPhotoFilter] = useState<PhotoFilter>("all");
    const [recipientCustomFilters, setRecipientCustomFilters] = useState<RecipientCustomFilter[]>([]);
    const [recipientCustomFilterMatch, setRecipientCustomFilterMatch] = useState<RecipientCustomFilterMatch>("all");
    const [templateLanguage, setTemplateLanguage] = useState<TemplateLanguage>("en");
    const [sendSummary, setSendSummary] = useState<string | null>(null);
    const [sendResults, setSendResults] = useState<TemplateSendResult[]>([]);
    const [dbSyncLoading, setDbSyncLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createForm, setCreateForm] = useState<CreateTemplateForm>(initialCreateTemplateForm);
    const [createHeaderFile, setCreateHeaderFile] = useState<File | null>(null);
    const [metaTemplatesOpen, setMetaTemplatesOpen] = useState(false);
    const [metaTemplatesLoading, setMetaTemplatesLoading] = useState(false);
    const [metaTemplates, setMetaTemplates] = useState<MetaTemplateRow[]>([]);
    const [metaTemplatesSearch, setMetaTemplatesSearch] = useState("");
    const [viewMetaTemplate, setViewMetaTemplate] = useState<MetaTemplateRow | null>(null);
    const [editMetaTemplate, setEditMetaTemplate] = useState<MetaTemplateRow | null>(null);
    const [editMetaLoading, setEditMetaLoading] = useState(false);
    const [editMetaForm, setEditMetaForm] = useState<EditMetaTemplateForm>({
        category: "UTILITY",
        body: "",
        bodyExamples: "",
        headerText: "",
        footerText: "",
        buttonText: "",
        buttonUrl: "",
    });

    const templates = useMemo(
        () => mergeTemplateMaps(mergeTemplateMaps(restTemplates, wsTemplates), dbTemplates),
        [restTemplates, wsTemplates, dbTemplates]
    );

    const loadTemplatesFromApi = useCallback(async () => {
        try {
            const response = await GetWhatsappTemplates();
            const payload = response?.data ?? response;
            const catalog = payload?.templates;
            setRestTemplates(normalizeTemplateCatalog(catalog));
        } catch (error) {
            console.error("Failed to load WhatsApp templates from API", error);
        }
    }, []);

    const loadDbTemplates = useCallback(async () => {
        try {
            const response = await GetWhatsappDbTemplates({ active_only: true, limit: 500 });
            const payload = response?.data ?? response;
            const list = payload?.templates || payload?.data?.templates || [];
            if (!Array.isArray(list)) return;
            setDbTemplates(normalizeTemplateCatalog(list));
        } catch (error) {
            console.error("Failed to load DB WhatsApp templates", error);
        }
    }, []);

    const loadTemplateFieldSources = useCallback(async () => {
        try {
            const response = await GetWhatsappTemplateFieldSources();
            const payload = response?.data ?? response;
            if (Array.isArray(payload?.sources) && payload.sources.length > 0) {
                setTemplateFieldSources(
                    payload.sources.map((source: any) => ({
                        value: String(source.value || ""),
                        label: `${String(source.label || source.value || "")}${source.is_user_linked === false ? " (not user-linked)" : ""}`,
                        table: String(source.table || String(source.value || "").split(".")[0] || ""),
                        column: String(source.column || String(source.value || "").split(".").slice(1).join(".") || ""),
                    })).filter((source: TemplateFieldSource) => source.value && source.label),
                );
            }
        } catch (error) {
            console.error("Failed to load WhatsApp template field sources", error);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
        loadTemplatesFromApi();
        loadDbTemplates();
        loadTemplateFieldSources();
    }, [fetchTemplates, loadTemplatesFromApi, loadDbTemplates, loadTemplateFieldSources, isConnected]);

    const selectedTemplate = selectedTemplateKey ? templates[selectedTemplateKey] : undefined;

    const serializedCustomFilters = useMemo(() => (
        recipientCustomFilters
            .filter((filter) => {
                if (!filter.source) return false;
                if (operatorNeedsRightColumn(filter.operator)) return Boolean(filter.rightSource);
                if (operatorNeedsValue(filter.operator)) return Boolean(filter.value.trim());
                return true;
            })
            .map((filter) => ({
                source: filter.source,
                operator: filter.operator,
                value: filter.operator === "between"
                    ? [filter.value.trim(), filter.valueTo.trim()]
                    : filter.value.trim(),
                right_source: filter.rightSource || undefined,
            }))
    ), [recipientCustomFilters]);

    const recipientFilterPayload = useMemo(() => ({
        active_only: activeOnly,
        gender_filter: genderFilter,
        premium_filter: premiumFilter,
        community_filter: communityFilter,
        user_status_filter: userStatusFilter,
        photo_filter: photoFilter,
        custom_filters: serializedCustomFilters,
        custom_filter_match: recipientCustomFilterMatch,
    }), [activeOnly, genderFilter, premiumFilter, communityFilter, userStatusFilter, photoFilter, serializedCustomFilters, recipientCustomFilterMatch]);

    useEffect(() => {
        if (!selectedTemplateKey || recipientMode === "phone") {
            setRecipientCount(null);
            setRecipientCountLoading(false);
            return;
        }

        let payload: Record<string, unknown> | null = null;
        if (recipientMode === "all") {
            payload = {
                recipient_mode: "all",
                ...recipientFilterPayload,
            };
        } else if (recipientMode === "search") {
            if (selectedUserIds.length > 0) {
                payload = {
                    recipient_mode: "selected",
                    user_ids: selectedUserIds,
                    ...recipientFilterPayload,
                };
            } else {
                const query = userSearchQuery.trim();
                if (!query) {
                    setRecipientCount(0);
                    setSendLimit(0);
                    setRecipientCountLoading(false);
                    return;
                }
                payload = {
                    recipient_mode: "search",
                    search: query,
                    ...recipientFilterPayload,
                };
            }
        }

        if (!payload) return;

        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setRecipientCountLoading(true);
            try {
                const response = await PostWhatsappRecipientCount(payload);
                if (cancelled) return;
                const result = response?.data ?? response;
                const count = Number(result?.count ?? 0);
                setRecipientCount(count);
                setSendLimit(count);
            } catch (error) {
                if (!cancelled) {
                    setRecipientCount(null);
                    console.error("Failed to count WhatsApp recipients", error);
                    toast.error("Failed to count matching recipients");
                }
            } finally {
                if (!cancelled) {
                    setRecipientCountLoading(false);
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [selectedTemplateKey, recipientMode, userSearchQuery, selectedUserIds, recipientFilterPayload]);

    const resetSendState = () => {
        setPhoneNumber("");
        setRecipientName("");
        setTemplateParams({});
        setTemplateValueMappings({});
        setTemplateSendConditions([]);
        setRecipientMode("phone");
        setUserSearchQuery("");
        setSearchResults([]);
        setSelectedUserIds([]);
        setSelectedUsersMap({});
        setSendSummary(null);
        setSendResults([]);
        setRecipientCount(null);
        setRecipientCountLoading(false);
        setActiveOnly(true);
        setSendLimit(500);
        setGenderFilter("all");
        setPremiumFilter("all");
        setCommunityFilter("all");
        setUserStatusFilter("active");
        setPhotoFilter("all");
        setRecipientCustomFilters([]);
        setRecipientCustomFilterMatch("all");
        setTemplateLanguage("en");
    };

    const openSendModal = (key: string, template: WhatsappTemplateSchema) => {
        const defaults: Record<string, string> = {};
        const mappings: Record<string, TemplateValueMapping> = {};
        template.parameters?.forEach((parameter) => {
            defaults[parameter.key] = parameter.default || "";
            mappings[parameter.key] = {
                mode: "manual",
                source: "",
                table: "",
                value: parameter.default || "",
                default: parameter.default || "",
            };
        });

        setSelectedTemplateKey(key);
        setPhoneNumber("");
        setRecipientName("");
        setTemplateParams(defaults);
        setTemplateValueMappings(mappings);
        setTemplateSendConditions([]);
        setRecipientMode("phone");
        setUserSearchQuery("");
        setSearchResults([]);
        setSelectedUserIds([]);
        setSelectedUsersMap({});
        setSendSummary(null);
        setSendResults([]);
        setRecipientCount(null);
        setRecipientCountLoading(false);
        setActiveOnly(true);
        setSendLimit(500);
        setGenderFilter("all");
        setPremiumFilter("all");
        setCommunityFilter("all");
        setUserStatusFilter("active");
        setPhotoFilter("all");
        setRecipientCustomFilters([]);
        setRecipientCustomFilterMatch("all");
        setTemplateLanguage((template.language_code || "").toLowerCase().startsWith("hi") ? "hi" : "en");
    };

    const closeSendModal = () => {
        setSelectedTemplateKey("");
        resetSendState();
    };

    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return Object.entries(templates)
            .map(([key, template]) => ({
                key,
                template,
                preview: buildTemplatePreview(template),
            }))
            .filter(({ key, template, preview }) => {
                const isAutomatic = isTemplateAutomatic(template, key);
                const isDbTemplate = isDbBackedTemplate(template);
                if (triggerFilter === "automatic" && !isAutomatic) return false;
                if (triggerFilter === "manual" && isAutomatic) return false;
                if (sourceFilter === "db" && !isDbTemplate) return false;
                if (sourceFilter === "code" && isDbTemplate) return false;
                if (!query) return true;
                return [
                    key,
                    template.template_name,
                    template.description || "",
                    template.language_code,
                    template.trigger_source || "",
                    template.trigger_type || "",
                    preview,
                ].some((value) => value.toLowerCase().includes(query));
            })
            .sort((a, b) => a.key.localeCompare(b.key));
    }, [templates, search, triggerFilter, sourceFilter]);

    const triggerCounts = useMemo(() => {
        const values = Object.entries(templates);
        const automatic = values.filter(([key, template]) => 
            template.trigger_type === "automatic" || template.is_automatic === true || isAutomaticTemplate(key)
        ).length;
        return {
            all: values.length,
            automatic,
            manual: values.length - automatic,
        };
    }, [templates]);

    const sourceCounts = useMemo(() => {
        const values = Object.values(templates);
        const db = values.filter((template) => isDbBackedTemplate(template)).length;
        return {
            all: values.length,
            db,
            code: values.length - db,
        };
    }, [templates]);

    const filteredMetaTemplates = useMemo(() => {
        const query = metaTemplatesSearch.trim().toLowerCase();
        if (!query) return metaTemplates;
        return metaTemplates.filter((template) =>
            [
                template.name,
                template.status || "",
                template.category || "",
                template.language || "",
                template.rejected_reason || "",
            ].some((value) => value.toLowerCase().includes(query))
        );
    }, [metaTemplates, metaTemplatesSearch]);

    const sendPreviewParams = useMemo(() => {
        return Object.entries(templateValueMappings).reduce((acc, [key, mapping]) => {
            if (mapping.mode === "field") {
                const label = templateFieldSources.find((source) => source.value === mapping.source)?.label || "User Field";
                acc[key] = `<${label}>`;
            } else {
                acc[key] = templateParams[key] || mapping.value || "";
            }
            return acc;
        }, { ...templateParams } as Record<string, string>);
    }, [templateParams, templateValueMappings]);

    const templateSourceTables = useMemo(() => {
        return Array.from(
            new Set(templateFieldSources.map((source) => source.table || source.value.split(".")[0]).filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b));
    }, [templateFieldSources]);

    const copyTemplate = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Template message copied");
        } catch {
            toast.error("Could not copy template");
        }
    };

    const handlePhoneChange = (value: string) => {
        setPhoneNumber(value.replace(/\D/g, "").slice(0, 10));
    };

    const validateTemplateParams = () => {
        if (!selectedTemplate) return false;
        const missingParam = selectedTemplate.parameters?.find((parameter) => {
            if (!parameter.required) return false;
            const mapping = templateValueMappings[parameter.key];
            if (mapping?.mode === "field") {
                return !mapping.table || !mapping.source;
            }
            return !templateParams[parameter.key]?.trim();
        });
        if (missingParam) {
            toast.error(`${missingParam.label || missingParam.key} is required`);
            return false;
        }
        return true;
    };

    const updateTemplateValueMapping = (
        key: string,
        patch: Partial<TemplateValueMapping>,
    ) => {
        setTemplateValueMappings((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                mode: prev[key]?.mode || "manual",
                source: prev[key]?.source || "",
                table: prev[key]?.table || "",
                value: templateParams[key] || "",
                default: prev[key]?.default || "",
                ...patch,
            },
        }));
    };

    const handleTemplateHeaderImageSelect = async (parameterKey: string, file?: File | null) => {
        if (!file) return;
        setUploadingTemplateParamKey(parameterKey);
        try {
            const response = await PostUploadWhatsappTemplateHeaderImage(file);
            const result = response?.data ?? response;
            if (!result?.status || !result?.url) {
                toast.error(result?.message || "Failed to upload image");
                return;
            }
            const url = String(result.url);
            setTemplateParams((prev) => ({
                ...prev,
                [parameterKey]: url,
            }));
            updateTemplateValueMapping(parameterKey, { mode: "manual", value: url });
            toast.success("Image selected");
        } catch (error: any) {
            toast.error(error?.message || "Failed to upload image");
        } finally {
            setUploadingTemplateParamKey(null);
        }
    };

    const runUserSearch = async (query: string) => {
        const trimmed = query.trim();
        if (!trimmed) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await SearchUsers(trimmed, 100, 0);
            const users = response?.data ?? [];
            const mapped: SearchUserRow[] = users.map((user: any) => ({
                id: user.id || user.user_id,
                name: user.name || user.id,
                phone: user.phone,
                status: user.status,
                location: user.location,
            }));
            setSearchResults(mapped);
        } catch (error: any) {
            console.error(error);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleUserSearchChange = (value: string) => {
        setUserSearchQuery(value);
        if (!value.trim()) {
            setSearchResults([]);
        }
    };

    const getSourcesForTable = (table?: string) =>
        templateFieldSources.filter((source) => (source.table || source.value.split(".")[0]) === table);

    const updateRecipientCustomFilter = (id: string, patch: Partial<RecipientCustomFilter>) => {
        setRecipientCustomFilters((prev) =>
            prev.map((filter) => {
                if (filter.id !== id) return filter;
                const next = { ...filter, ...patch };
                if (patch.table !== undefined) {
                    next.source = "";
                }
                if (patch.rightTable !== undefined) {
                    next.rightSource = "";
                }
                if (patch.operator !== undefined) {
                    next.value = "";
                    next.valueTo = "";
                    next.rightSource = "";
                    next.rightTable = "";
                }
                return next;
            }),
        );
    };

    const removeRecipientCustomFilter = (id: string) => {
        setRecipientCustomFilters((prev) => prev.filter((filter) => filter.id !== id));
    };

    const toggleUserSelection = (userId: string, user?: SearchUserRow) => {
        setSelectedUserIds((prev) => {
            const updated = prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId];
            if (user && updated.includes(userId)) {
                setSelectedUsersMap((m) => ({ ...m, [userId]: user }));
            }
            return updated;
        });
    };

    const toggleSelectAllSearchResults = () => {
        const currentResultIds = searchResults.map((user) => user.id);
        const allCurrentSelected = currentResultIds.every((id) => selectedUserIds.includes(id));
        
        if (allCurrentSelected) {
            setSelectedUserIds((prev) => prev.filter((id) => !currentResultIds.includes(id)));
        } else {
            const newMap = { ...selectedUsersMap };
            searchResults.forEach((user) => {
                newMap[user.id] = user;
            });
            setSelectedUsersMap(newMap);
            setSelectedUserIds((prev) => {
                const filtered = prev.filter((id) => !currentResultIds.includes(id));
                return [...filtered, ...currentResultIds];
            });
        }
    };

    const sendSelectedTemplate = async () => {
        if (!selectedTemplate || !selectedTemplateKey) return;
        if (!validateTemplateParams()) return;
        if (recipientMode !== "phone" && sendLimit < 1) {
            toast.error("No matching recipients found");
            return;
        }

        const valueMappings = Object.entries(templateValueMappings).reduce(
            (acc, [key, mapping]) => {
                acc[key] = {
                    mode: mapping.mode,
                    source: mapping.source,
                    table: mapping.table,
                    value: templateParams[key] || mapping.value || "",
                    default: mapping.default || "",
                };
                return acc;
            },
            {} as Record<string, TemplateValueMapping>,
        );

        let payload: Record<string, unknown> = {
            template_key: selectedTemplateKey,
            template_values: templateParams,
            value_mappings: valueMappings,
            send_conditions: templateSendConditions.filter((condition) => condition.left && condition.right),
            active_only: activeOnly,
            limit: sendLimit,
            template_language: templateLanguage,
            gender_filter: genderFilter,
            premium_filter: premiumFilter,
            community_filter: communityFilter,
            user_status_filter: userStatusFilter,
            photo_filter: photoFilter,
            custom_filters: serializedCustomFilters,
            custom_filter_match: recipientCustomFilterMatch,
        };

        if (recipientMode === "phone") {
            if (phoneNumber.length !== 10) {
                toast.error("Enter a valid 10 digit phone number");
                return;
            }
            if (!recipientName.trim()) {
                toast.error("Recipient name is required");
                return;
            }
            payload = {
                ...payload,
                recipient_mode: "phone",
                phone: phoneNumber,
                recipient_name: recipientName.trim(),
            };
        } else if (recipientMode === "search") {
            if (selectedUserIds.length > 0) {
                payload = {
                    ...payload,
                    recipient_mode: "selected",
                    user_ids: selectedUserIds,
                };
            } else {
                const query = userSearchQuery.trim();
                if (!query) {
                    toast.error("Search for users or select at least one user");
                    return;
                }
                payload = {
                    ...payload,
                    recipient_mode: "search",
                    search: query,
                };
            }
        } else {
            const confirmed = window.confirm(
                `Send "${selectedTemplate.template_name}" to up to ${sendLimit} active users with phone numbers?`
            );
            if (!confirmed) return;
            payload = {
                ...payload,
                recipient_mode: "all",
            };
        }

        setSendLoading(true);
        setSendSummary(null);
        setSendResults([]);
        try {
            const isDbTemplate = isDbBackedTemplate(selectedTemplate);
            const dbTemplateIdOrKey = selectedTemplate.id || selectedTemplate.template_key || selectedTemplate.key || selectedTemplateKey;

            const sendRequest = async (requestPayload: Record<string, unknown>) => {
                if (isDbTemplate) {
                    return await PostSendWhatsappDbTemplateBulk(dbTemplateIdOrKey, {
                        values: templateParams,
                        value_mappings: valueMappings,
                        send_conditions: templateSendConditions.filter((condition) => condition.left && condition.right),
                        recipient_mode: requestPayload.recipient_mode,
                        phone: requestPayload.phone,
                        recipient_name: requestPayload.recipient_name,
                        user_ids: requestPayload.user_ids,
                        search: requestPayload.search,
                        active_only: activeOnly,
                        limit: requestPayload.limit || sendLimit,
                        template_language: templateLanguage,
                        gender_filter: genderFilter,
                        premium_filter: premiumFilter,
                        community_filter: communityFilter,
                        user_status_filter: userStatusFilter,
                        photo_filter: photoFilter,
                        custom_filters: serializedCustomFilters,
                        custom_filter_match: recipientCustomFilterMatch,
                    });
                }
                return await PostSendWhatsappTemplateBulk(requestPayload);
            };

            if (payload.recipient_mode === "phone") {
                const response = await sendRequest(payload);
                const result = response?.data ?? response;
                const sent = result?.sent ?? 0;
                const total = result?.total ?? 0;
                const failed = result?.failed ?? 0;
                const summary = result?.message || `Sent ${sent}/${total}, failed ${failed}`;
                setSendSummary(summary);
                setSendResults(Array.isArray(result?.results) ? result.results : []);
                if (sent > 0) toast.success(summary);
                else toast.error(summary);
                return;
            }

            let recipients: Array<{ user_id?: string; name?: string; phone?: string }> = [];
            if (Array.isArray(payload.user_ids) && payload.user_ids.length > 0) {
                recipients = payload.user_ids.map((userId) => ({
                    user_id: String(userId),
                    name: selectedUsersMap[String(userId)]?.name || String(userId),
                    phone: selectedUsersMap[String(userId)]?.phone || "",
                }));
            } else {
                const recipientResponse = await GetWhatsappTemplateRecipients({
                    recipient_mode: payload.recipient_mode,
                    search: payload.search,
                    active_only: activeOnly,
                    limit: sendLimit,
                    gender_filter: genderFilter,
                    premium_filter: premiumFilter,
                    community_filter: communityFilter,
                    user_status_filter: userStatusFilter,
                    photo_filter: photoFilter,
                    custom_filters: serializedCustomFilters,
                    custom_filter_match: recipientCustomFilterMatch,
                });
                const recipientResult = recipientResponse?.data ?? recipientResponse;
                recipients = Array.isArray(recipientResult?.recipients) ? recipientResult.recipients : [];
            }

            if (recipients.length === 0) {
                setSendSummary("No recipients found with a valid phone number");
                toast.error("No recipients found with a valid phone number");
                return;
            }

            let sentCount = 0;
            let failedCount = 0;
            const accumulatedResults: TemplateSendResult[] = [];

            for (let index = 0; index < recipients.length; index += 1) {
                const recipient = recipients[index];
                if (!recipient.user_id) {
                    failedCount += 1;
                    const missingResult: TemplateSendResult = {
                        name: recipient.name,
                        phone: recipient.phone,
                        success: false,
                        skipped: true,
                        error: "Missing user_id for sequential send",
                    };
                    accumulatedResults.push(missingResult);
                    setSendResults([...accumulatedResults]);
                    setSendSummary(`Processed ${index + 1}/${recipients.length}: sent ${sentCount}, failed ${failedCount}`);
                    continue;
                }

                const response = await sendRequest({
                    ...payload,
                    recipient_mode: "selected",
                    user_ids: [recipient.user_id],
                    limit: 1,
                });
                const result = response?.data ?? response;
                const rowResults = Array.isArray(result?.results) && result.results.length > 0
                    ? result.results
                    : [{
                        user_id: recipient.user_id,
                        name: recipient.name,
                        phone: recipient.phone,
                        success: Boolean(result?.sent),
                        response: result?.response,
                        error: result?.message,
                    }];

                rowResults.forEach((row: TemplateSendResult) => {
                    if (row.success) sentCount += 1;
                    else failedCount += 1;
                    accumulatedResults.push(row);
                });

                const summary = `Processed ${index + 1}/${recipients.length}: sent ${sentCount}, failed ${failedCount}`;
                setSendResults([...accumulatedResults]);
                setSendSummary(summary);
            }

            const finalSummary = `Send complete: ${sentCount}/${recipients.length} sent, ${failedCount} failed`;
            setSendSummary(finalSummary);
            if (sentCount > 0) toast.success(finalSummary);
            else toast.error(finalSummary);
        } catch (error: any) {
            toast.error(error?.message || "Failed to send template");
        } finally {
            setSendLoading(false);
        }
    };

    const updateCreateForm = (field: keyof CreateTemplateForm, value: string) => {
        setCreateForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateCreateBodyExample = (index: number, value: string) => {
        setCreateForm((prev) => {
            const count = Math.max(getBodyPlaceholderIndexes(prev.body).length, index + 1);
            const values = Array.from({ length: count }, (_, valueIndex) =>
                prev.bodyExamples.split("\n")[valueIndex] || "",
            );
            values[index] = value;
            return { ...prev, bodyExamples: values.join("\n") };
        });
    };

    const updateCreateHeaderType = (value: HeaderType) => {
        setCreateForm((prev) => ({
            ...prev,
            headerType: value,
            headerText: value === "TEXT" ? prev.headerText : "",
            headerSample: ["IMAGE", "VIDEO", "DOCUMENT"].includes(value) ? prev.headerSample : "",
            headerFileName: ["IMAGE", "VIDEO", "DOCUMENT"].includes(value) ? prev.headerFileName : "",
        }));
        if (!["IMAGE", "VIDEO", "DOCUMENT"].includes(value)) {
            setCreateHeaderFile(null);
        }
    };

    const addCreateButton = () => {
        setCreateForm((prev) => ({
            ...prev,
            buttons: [
                ...prev.buttons,
                { type: "QUICK_REPLY", text: "", url: "", phone_number: "" },
            ],
        }));
    };

    const updateCreateButton = (
        index: number,
        field: keyof CreateTemplateButton,
        value: string,
    ) => {
        setCreateForm((prev) => ({
            ...prev,
            buttons: prev.buttons.map((button, buttonIndex) =>
                buttonIndex === index ? { ...button, [field]: value } : button,
            ),
        }));
    };

    const removeCreateButton = (index: number) => {
        setCreateForm((prev) => ({
            ...prev,
            buttons: prev.buttons.filter((_, buttonIndex) => buttonIndex !== index),
        }));
    };

    const closeCreateModal = () => {
        setCreateModalOpen(false);
        setCreateForm(initialCreateTemplateForm);
        setCreateHeaderFile(null);
    };

    const fetchMetaTemplates = async () => {
        setMetaTemplatesLoading(true);
        try {
            const response = await GetWhatsappMetaTemplates(250);
            const result = response?.data ?? response;
            if (Array.isArray(result?.templates)) {
                setMetaTemplates(result.templates);
                setMetaTemplatesOpen(true);
            } else {
                toast.error(result?.response?.error?.message || result?.message || "Failed to fetch Meta templates");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch Meta templates");
        } finally {
            setMetaTemplatesLoading(false);
        }
    };

    const syncDbTemplatesFromMeta = async () => {
        setDbSyncLoading(true);
        try {
            const response = await PostSyncWhatsappDbTemplatesFromMeta(250);
            const result = response?.data ?? response;
            if (result?.status) {
                toast.success(result?.message || "Meta templates synced to database");
                await loadDbTemplates();
            } else {
                toast.error(result?.response?.error?.message || result?.message || "Failed to sync Meta templates");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to sync Meta templates");
        } finally {
            setDbSyncLoading(false);
        }
    };

    const openEditMetaTemplate = (template: MetaTemplateRow) => {
        const body = getMetaComponent(template, "BODY");
        const header = getMetaComponent(template, "HEADER");
        const footer = getMetaComponent(template, "FOOTER");
        const buttons = getMetaComponent(template, "BUTTONS");
        const firstButton = Array.isArray(buttons?.buttons) ? buttons?.buttons[0] : null;
        setEditMetaTemplate(template);
        setEditMetaForm({
            category: template.category || "UTILITY",
            body: String(body?.text || ""),
            bodyExamples: getBodyExamples(body),
            headerText: String(header?.text || ""),
            footerText: String(footer?.text || ""),
            buttonText: String(firstButton?.text || ""),
            buttonUrl: String(firstButton?.url || ""),
        });
    };

    const submitEditMetaTemplate = async () => {
        if (!editMetaTemplate?.id) {
            toast.error("Template ID is missing");
            return;
        }
        if (!editMetaForm.body.trim()) {
            toast.error("Body is required");
            return;
        }
        const placeholderCount = getBodyPlaceholderCount(editMetaForm.body);
        const examples = editMetaForm.bodyExamples.split("\n").map((value) => value.trim()).filter(Boolean);
        if (placeholderCount > 0 && examples.length < placeholderCount) {
            toast.error(`Add ${placeholderCount} body example value${placeholderCount > 1 ? "s" : ""}`);
            return;
        }

        setEditMetaLoading(true);
        try {
            const response = await PatchWhatsappMetaTemplate(editMetaTemplate.id, {
                category: editMetaForm.category,
                components: buildMetaComponents(editMetaForm),
            });
            const result = response?.data ?? response;
            if (result?.status) {
                toast.success(result?.message || "Template updated");
                setEditMetaTemplate(null);
                await fetchMetaTemplates();
            } else {
                toast.error(result?.response?.error?.message || result?.message || "Failed to update template");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to update template");
        } finally {
            setEditMetaLoading(false);
        }
    };

    const deleteMetaTemplate = async (template: MetaTemplateRow) => {
        if (!template.name) {
            toast.error("Template name is missing");
            return;
        }
        const confirmed = window.confirm(`Delete "${template.name}" from Meta WhatsApp Manager?`);
        if (!confirmed) return;

        try {
            const response = await DeleteWhatsappMetaTemplate(template.name);
            const result = response?.data ?? response;
            if (result?.status) {
                toast.success(result?.message || "Template deleted");
                await fetchMetaTemplates();
            } else {
                toast.error(result?.response?.error?.message || result?.message || "Failed to delete template");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to delete template");
        }
    };

    const submitCreateTemplate = async () => {
        const name = createForm.name.trim().toLowerCase();
        const body = createForm.body.trim();
        if (!name) {
            toast.error("Template name is required");
            return;
        }
        if (!/^[a-z0-9_]+$/.test(name)) {
            toast.error("Use lowercase letters, numbers, and underscores in template name");
            return;
        }
        if (!body) {
            toast.error("Template body is required");
            return;
        }

        const placeholderIndexes = getBodyPlaceholderIndexes(body);
        const exampleLines = createForm.bodyExamples.split("\n");
        const examples = placeholderIndexes.map((_, index) => (exampleLines[index] || "").trim());
        const missingExampleIndex = examples.findIndex((value) => !value);
        if (placeholderIndexes.length > 0 && missingExampleIndex >= 0) {
            toast.error(`Add body example value for {{${placeholderIndexes[missingExampleIndex]}}}`);
            return;
        }
        if (createForm.headerType === "TEXT" && !createForm.headerText.trim()) {
            toast.error("Header text is required for text header");
            return;
        }
        const hasMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(createForm.headerType);
        if (hasMediaHeader && !createForm.headerSample.trim() && !createHeaderFile) {
            toast.error("Select a header sample file");
            return;
        }

        const buttons = createForm.buttons
            .map((button) => ({
                type: button.type,
                text: button.text.trim(),
                url: button.url.trim(),
                phone_number: button.phone_number.trim(),
            }))
            .filter((button) => button.text);

        const invalidButton = buttons.find((button) =>
            (button.type === "URL" && !button.url) ||
            (button.type === "PHONE_NUMBER" && !button.phone_number)
        );
        if (invalidButton) {
            toast.error("Complete URL or phone number for each button");
            return;
        }

        setCreateLoading(true);
        try {
            let headerSample = createForm.headerSample.trim();
            if (hasMediaHeader && !headerSample && createHeaderFile) {
                const uploadResponse = await PostWhatsappTemplateHeaderSample(createHeaderFile);
                const uploadResult = uploadResponse?.data ?? uploadResponse;
                if (!uploadResult?.status || !uploadResult?.handle) {
                    toast.error(uploadResult?.response?.error?.message || uploadResult?.message || "Failed to upload header sample");
                    setCreateLoading(false);
                    return;
                }
                headerSample = uploadResult.handle;
                setCreateForm((prev) => ({ ...prev, headerSample }));
            }

            const payload = {
                name,
                category: createForm.category,
                language: createForm.language.trim() || "en",
                body,
                body_examples: examples,
                header_type: createForm.headerType,
                header_text: createForm.headerText.trim() || undefined,
                header_sample: headerSample || undefined,
                footer_text: createForm.footerText.trim() || undefined,
                buttons: buttons.length ? buttons : undefined,
                allow_category_change: true,
            };
            const response = await PostCreateWhatsappTemplate(payload);
            const result = response?.data ?? response;
            if (result?.status) {
                toast.success(result?.message || "Template submitted to Meta");
                closeCreateModal();
            } else {
                toast.error(result?.response?.error?.message || result?.message || "Failed to create template");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to create template");
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-6rem)] bg-slate-950 text-slate-100 rounded-xl overflow-hidden border border-slate-800/80 shadow-2xl">
            <div className="bg-slate-900/95 border-b border-slate-800">
                <div className="px-6 py-5 flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3 text-slate-100">
                            <div className="h-12 w-12 rounded-xl bg-blue-500/15 border border-blue-400/30 flex items-center justify-center shadow-lg shadow-blue-950/20">
                                <FileText className="w-5 h-5 text-blue-300" />
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                                    WhatsApp Console
                                </p>
                                <h2 className="text-2xl font-bold text-white leading-tight">Template Catalog</h2>
                            </div>
                        </div>
                        <p className="hidden">
                            {templatesCount || Object.keys(templates).length} templates — send to a phone, search users, or all active users
                        </p>
                        <p className="text-sm text-slate-400 mt-3 max-w-2xl">
                            Review, copy, sync, and send templates from one place. {sourceCounts.db} templates are loaded from the template database.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 w-full xl:w-auto">
                        {([
                            ["All", triggerCounts.all, "text-blue-300"],
                            ["Automatic", triggerCounts.automatic, "text-emerald-300"],
                            ["Manual", triggerCounts.manual, "text-amber-300"],
                            ["DB", sourceCounts.db, "text-indigo-300"],
                            ["Code", sourceCounts.code, "text-slate-300"],
                        ] as Array<[string, number, string]>).map(([label, value, color]) => (
                            <div key={label} className="rounded-lg border border-slate-800 bg-slate-950/70 px-4 py-2.5 min-w-[104px]">
                                <div className={`text-lg font-bold leading-5 ${color}`}>{value}</div>
                                <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="px-6 pb-5 flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                    <div className="flex flex-col lg:flex-row gap-2">
                        <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/70 p-1">
                            <span className="px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 shrink-0">Type</span>
                            <div className="grid grid-cols-3 rounded-md bg-slate-950">
                                {([
                                    ["all", `All ${triggerCounts.all}`],
                                    ["automatic", `Auto ${triggerCounts.automatic}`],
                                    ["manual", `Manual ${triggerCounts.manual}`],
                                ] as Array<[TriggerFilter, string]>).map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setTriggerFilter(value)}
                                        className={`px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                                            triggerFilter === value
                                                ? "bg-blue-600 text-white"
                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/70 p-1">
                            <span className="px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 shrink-0">Source</span>
                            <div className="grid grid-cols-3 rounded-md bg-slate-950">
                                {([
                                    ["all", `All ${sourceCounts.all}`],
                                    ["db", `DB ${sourceCounts.db}`],
                                    ["code", `Code ${sourceCounts.code}`],
                                ] as Array<[SourceFilter, string]>).map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setSourceFilter(value)}
                                        className={`px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                                            sourceFilter === value
                                                ? "bg-indigo-600 text-white"
                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col lg:flex-row gap-3 w-full 2xl:max-w-[860px]">
                        <div className="grid grid-cols-3 gap-2 lg:flex lg:shrink-0">
                            <button
                                type="button"
                                onClick={syncDbTemplatesFromMeta}
                                disabled={dbSyncLoading}
                                className="inline-flex h-10 items-center justify-center gap-2 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-semibold transition-colors shadow-lg shadow-indigo-950/30 whitespace-nowrap"
                                title="Sync Meta to DB"
                            >
                                {dbSyncLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <FileText className="w-4 h-4 shrink-0" />}
                                <span>Sync DB</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreateModalOpen(true)}
                                className="inline-flex h-10 items-center justify-center gap-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors shadow-lg shadow-emerald-950/30 whitespace-nowrap"
                            >
                                <Plus className="w-4 h-4 shrink-0" />
                                <span>Create</span>
                            </button>
                            <button
                                type="button"
                                onClick={fetchMetaTemplates}
                                disabled={metaTemplatesLoading}
                                className="inline-flex h-10 items-center justify-center gap-2 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-white text-xs font-semibold transition-colors border border-slate-700 whitespace-nowrap"
                                title="Meta Templates"
                            >
                                {metaTemplatesLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <FileText className="w-4 h-4 shrink-0" />}
                                <span>Meta</span>
                            </button>
                        </div>
                        <div className="relative w-full min-w-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search name, key, language, body..."
                                className="h-10 w-full pl-9 pr-3 text-sm bg-slate-950 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="overflow-y-auto p-5">
                {rows.length === 0 ? (
                    <div className="h-56 flex flex-col items-center justify-center text-sm text-slate-500 border border-dashed border-slate-800 rounded-xl bg-slate-900/40">
                        <FileText className="w-8 h-8 text-slate-600 mb-3" />
                        <span>No templates found</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {rows.map(({ key, template, preview }) => (
                            <article
                                key={key}
                                className="group border border-slate-800 bg-slate-900/80 rounded-xl p-4 min-w-0 hover:border-blue-500/50 hover:bg-slate-900 transition-colors shadow-lg shadow-slate-950/20"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="text-base font-semibold text-white break-words">
                                                {template.template_name}
                                            </h3>
                                            <span
                                                className={`text-[10px] px-2.5 py-1 rounded-full border font-semibold ${
                                                    isTemplateAutomatic(template, key)
                                                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                                        : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                                                }`}
                                            >
                                                {isTemplateAutomatic(template, key) ? "Automatic" : "Manual"}
                                            </span>
                                            <span
                                                className={`text-[10px] px-2.5 py-1 rounded-full border font-semibold ${
                                                    isDbBackedTemplate(template)
                                                        ? "bg-indigo-500/10 text-indigo-300 border-indigo-500/30"
                                                        : "bg-slate-800 text-slate-300 border-slate-700"
                                                }`}
                                            >
                                                {isDbBackedTemplate(template) ? "DB" : "Code"}
                                            </span>
                                            {template.status && (
                                                <span className="text-[10px] px-2.5 py-1 rounded-full border font-semibold bg-blue-500/10 text-blue-300 border-blue-500/30">
                                                    {template.status}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                                            <span className="font-mono text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-0.5">{key}</span>
                                            <span className="uppercase tracking-wide">{template.language_code}</span>
                                            {template.category && (
                                                <span className="uppercase tracking-wide">{template.category}</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => copyTemplate(preview)}
                                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors shrink-0"
                                        title="Copy full message"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>

                                {template.description && (
                                    <p className="mt-3 text-sm text-slate-400 leading-5">{template.description}</p>
                                )}
                                {template.trigger_source && (
                                    <p className="mt-1 text-[11px] text-slate-500">
                                        Source: {template.trigger_source}
                                    </p>
                                )}

                                <pre className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-slate-200 bg-slate-950/80 border border-slate-800 rounded-lg p-4 font-sans max-h-72 overflow-y-auto">
                                    {preview}
                                </pre>

                                {template.parameters?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {template.parameters.map((parameter) => (
                                            <span
                                                key={parameter.key}
                                                className="text-[11px] px-2 py-1 rounded-md bg-slate-800/90 text-slate-300 border border-slate-700"
                                            >
                                                {parameter.label || parameter.key}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => openSendModal(key, template)}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors shadow-lg shadow-blue-950/30"
                                    >
                                        <Send className="w-4 h-4" />
                                        Send Template
                                    </button>
                                </div>
                            </article>
                            
                        ))}
                    </div>
                )}
            </div>

            {metaTemplatesOpen && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl flex flex-col">
                        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800 bg-slate-900/95 shrink-0">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
                                    Meta WhatsApp Manager
                                </p>
                                <h3 className="text-xl font-bold text-white">Template Status</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    {metaTemplates.length} templates fetched from Meta with approval status and category.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setMetaTemplatesOpen(false)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-6 py-4 border-b border-slate-800 shrink-0">
                            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <div className="relative w-full sm:max-w-md">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        value={metaTemplatesSearch}
                                        onChange={(event) => setMetaTemplatesSearch(event.target.value)}
                                        placeholder="Search Meta templates"
                                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-slate-950 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={fetchMetaTemplates}
                                    disabled={metaTemplatesLoading}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                                >
                                    {metaTemplatesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {filteredMetaTemplates.length === 0 ? (
                                <div className="h-48 flex items-center justify-center text-sm text-slate-500">
                                    No Meta templates found
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="sticky top-0 bg-slate-900 border-b border-slate-800">
                                            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                                                <th className="px-5 py-3 font-semibold">Template</th>
                                                <th className="px-5 py-3 font-semibold">Status</th>
                                                <th className="px-5 py-3 font-semibold">Category</th>
                                                <th className="px-5 py-3 font-semibold">Language</th>
                                                <th className="px-5 py-3 font-semibold">Quality</th>
                                                <th className="px-5 py-3 font-semibold">Reason</th>
                                                <th className="px-5 py-3 font-semibold text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800">
                                            {filteredMetaTemplates.map((template) => {
                                                const status = String(template.status || "UNKNOWN").toUpperCase();
                                                const statusClass =
                                                    status === "APPROVED"
                                                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                                        : status === "REJECTED"
                                                          ? "bg-red-500/10 text-red-300 border-red-500/30"
                                                          : "bg-amber-500/10 text-amber-300 border-amber-500/30";
                                                return (
                                                    <tr key={template.id || `${template.name}-${template.language}`} className="text-slate-300">
                                                        <td className="px-5 py-3">
                                                            <div className="font-semibold text-white">{template.name}</div>
                                                            {template.id && <div className="text-[11px] text-slate-500 font-mono">{template.id}</div>}
                                                        </td>
                                                        <td className="px-5 py-3">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-full border text-[11px] font-semibold ${statusClass}`}>
                                                                {status}
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-3">{template.category || "-"}</td>
                                                        <td className="px-5 py-3">{template.language || "-"}</td>
                                                        <td className="px-5 py-3">{template.quality_score?.score || "-"}</td>
                                                        <td className="px-5 py-3 max-w-xs text-xs text-slate-400">
                                                            {template.rejected_reason || "-"}
                                                        </td>
                                                        <td className="px-5 py-3">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setViewMetaTemplate(template)}
                                                                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
                                                                    title="View content"
                                                                >
                                                                    <Eye className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openEditMetaTemplate(template)}
                                                                    className="p-2 rounded-lg text-blue-300 hover:text-white hover:bg-blue-600/20"
                                                                    title="Edit template"
                                                                >
                                                                    <Edit3 className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => deleteMetaTemplate(template)}
                                                                    className="p-2 rounded-lg text-red-300 hover:text-white hover:bg-red-600/20"
                                                                    title="Delete template"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {viewMetaTemplate && (
                <div className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl flex flex-col">
                        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800 shrink-0">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
                                    Template Content
                                </p>
                                <h3 className="text-xl font-bold text-white">{viewMetaTemplate.name}</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    {viewMetaTemplate.category || "-"} · {viewMetaTemplate.status || "-"} · {viewMetaTemplate.language || "-"}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setViewMetaTemplate(null)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
                            {(viewMetaTemplate.components || []).map((component, index) => (
                                <div key={`${component.type || "component"}-${index}`} className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-300 mb-3">
                                        {String(component.type || "Component")}
                                    </div>
                                    {component.text && (
                                        <pre className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-200 font-sans">
                                            {String(component.text)}
                                        </pre>
                                    )}
                                    {component.example && (
                                        <pre className="mt-3 whitespace-pre-wrap break-words text-xs leading-5 text-slate-300 bg-slate-900 border border-slate-800 rounded-lg p-3">
                                            {JSON.stringify(component.example, null, 2)}
                                        </pre>
                                    )}
                                    {component.buttons && (
                                        <pre className="mt-3 whitespace-pre-wrap break-words text-xs leading-5 text-slate-300 bg-slate-900 border border-slate-800 rounded-lg p-3">
                                            {JSON.stringify(component.buttons, null, 2)}
                                        </pre>
                                    )}
                                    {!component.text && !component.example && !component.buttons && (
                                        <pre className="whitespace-pre-wrap break-words text-xs leading-5 text-slate-300 bg-slate-900 border border-slate-800 rounded-lg p-3">
                                            {JSON.stringify(component, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {editMetaTemplate && (
                <div className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl flex flex-col">
                        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800 shrink-0">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
                                    Edit Meta Template
                                </p>
                                <h3 className="text-xl font-bold text-white">{editMetaTemplate.name}</h3>
                                <p className="text-xs text-slate-400 mt-1">Changes are submitted back to Meta for review.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditMetaTemplate(null)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-400 mb-2">Category</label>
                                <select
                                    value={editMetaForm.category}
                                    onChange={(event) => setEditMetaForm((prev) => ({ ...prev, category: event.target.value }))}
                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                >
                                    <option value="UTILITY">UTILITY</option>
                                    <option value="MARKETING">MARKETING</option>
                                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-400 mb-2">Body</label>
                                <textarea
                                    value={editMetaForm.body}
                                    onChange={(event) => setEditMetaForm((prev) => ({ ...prev, body: event.target.value }))}
                                    rows={7}
                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-400 mb-2">Body Example Values</label>
                                <textarea
                                    value={editMetaForm.bodyExamples}
                                    onChange={(event) => setEditMetaForm((prev) => ({ ...prev, bodyExamples: event.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Header Text</label>
                                    <input
                                        value={editMetaForm.headerText}
                                        onChange={(event) => setEditMetaForm((prev) => ({ ...prev, headerText: event.target.value }))}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Footer Text</label>
                                    <input
                                        value={editMetaForm.footerText}
                                        onChange={(event) => setEditMetaForm((prev) => ({ ...prev, footerText: event.target.value }))}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">URL Button Text</label>
                                    <input
                                        value={editMetaForm.buttonText}
                                        onChange={(event) => setEditMetaForm((prev) => ({ ...prev, buttonText: event.target.value }))}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">URL Button Link</label>
                                    <input
                                        value={editMetaForm.buttonUrl}
                                        onChange={(event) => setEditMetaForm((prev) => ({ ...prev, buttonUrl: event.target.value }))}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 bg-slate-900/95 shrink-0">
                            <button
                                type="button"
                                onClick={() => setEditMetaTemplate(null)}
                                className="px-4 py-2.5 text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitEditMetaTemplate}
                                disabled={editMetaLoading}
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg transition-colors"
                            >
                                {editMetaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl flex flex-col">
                        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800 bg-slate-900/95 shrink-0">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
                                    Meta WhatsApp Manager
                                </p>
                                <h3 className="text-xl font-bold text-white">Create Template</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    Submission goes to Meta for review. Approved templates still need to be added/synced into this catalog before sending.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeCreateModal}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Template Name *</label>
                                    <input
                                        value={createForm.name}
                                        onChange={(event) => updateCreateForm("name", event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                                        placeholder="new_offer_template"
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Category *</label>
                                    <select
                                        value={createForm.category}
                                        onChange={(event) => updateCreateForm("category", event.target.value)}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    >
                                        <option value="UTILITY">UTILITY</option>
                                        <option value="MARKETING">MARKETING</option>
                                        <option value="AUTHENTICATION">AUTHENTICATION</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Language *</label>
                                    <input
                                        value={createForm.language}
                                        onChange={(event) => updateCreateForm("language", event.target.value)}
                                        placeholder="en"
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Footer Text</label>
                                    <input
                                        value={createForm.footerText}
                                        onChange={(event) => updateCreateForm("footerText", event.target.value)}
                                        placeholder="Optional footer"
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">Header Type</label>
                                    <select
                                        value={createForm.headerType}
                                        onChange={(event) => updateCreateHeaderType(event.target.value as HeaderType)}
                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    >
                                        <option value="NONE">None</option>
                                        <option value="TEXT">Text</option>
                                        <option value="IMAGE">Image Sample</option>
                                        <option value="VIDEO">Video Sample</option>
                                        <option value="DOCUMENT">Document Sample</option>
                                    </select>
                                </div>
                            </div>

                            {createForm.headerType !== "NONE" && (
                                <div>
                                    {createForm.headerType === "TEXT" ? (
                                        <>
                                            <label className="block text-xs font-semibold text-slate-400 mb-2">Header Text</label>
                                            <input
                                                value={createForm.headerText}
                                                onChange={(event) => updateCreateForm("headerText", event.target.value)}
                                                placeholder="Optional title"
                                                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                            />
                                        </>
                                    ) : (
                                        <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-300">
                                                        Select {createForm.headerType.toLowerCase()} sample
                                                    </label>
                                                    <p className="mt-1 text-[11px] text-slate-500">
                                                        Choose the sample file used for this Meta header.
                                                    </p>
                                                </div>
                                                <span className="rounded-full border border-blue-400/25 bg-blue-500/10 px-2.5 py-1 text-[10px] font-bold text-blue-200">
                                                    {createForm.headerType}
                                                </span>
                                            </div>
                                            <label className="flex min-h-[88px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-700 bg-slate-950 px-4 py-4 text-center hover:border-blue-400/60 hover:bg-slate-900 transition-colors">
                                                <FileText className="h-5 w-5 text-blue-300" />
                                                <span className="text-sm font-semibold text-slate-200">
                                                    {createForm.headerFileName || `Select ${createForm.headerType.toLowerCase()} file`}
                                                </span>
                                                <span className="text-[11px] text-slate-500">
                                                    {createForm.headerType === "IMAGE" && "PNG or JPG sample"}
                                                    {createForm.headerType === "VIDEO" && "MP4 video sample"}
                                                    {createForm.headerType === "DOCUMENT" && "PDF document sample"}
                                                </span>
                                                <input
                                                    type="file"
                                                    accept={
                                                        createForm.headerType === "IMAGE"
                                                            ? "image/png,image/jpeg"
                                                            : createForm.headerType === "VIDEO"
                                                                ? "video/mp4"
                                                                : "application/pdf"
                                                    }
                                                    onChange={(event) => {
                                                        const file = event.target.files?.[0];
                                                        setCreateHeaderFile(file || null);
                                                        updateCreateForm("headerFileName", file?.name || "");
                                                        updateCreateForm("headerSample", "");
                                                    }}
                                                    className="hidden"
                                                />
                                            </label>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-2">Meta Sample Handle</label>
                                                <input
                                                    value={createForm.headerSample}
                                                    onChange={(event) => updateCreateForm("headerSample", event.target.value)}
                                                    placeholder="Auto-filled after upload, or paste existing handle"
                                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                />
                                                <p className="mt-2 text-[11px] text-slate-500">
                                                    Select a file and submit. We upload it to Meta first, then use the returned handle.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="block text-xs font-semibold text-slate-400">Body *</label>
                                    <span className="text-[11px] text-slate-500">
                                        {getBodyPlaceholderCount(createForm.body)} variable placeholders
                                    </span>
                                </div>
                                <textarea
                                    value={createForm.body}
                                    onChange={(event) => updateCreateForm("body", event.target.value)}
                                    rows={7}
                                    placeholder="Hi {{1}}, your request has been received."
                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="block text-xs font-semibold text-slate-400">Body Example Values</label>
                                    <span className="text-[11px] text-slate-500">
                                        One sample value for each body variable
                                    </span>
                                </div>
                                {getBodyPlaceholderIndexes(createForm.body).length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {getBodyPlaceholderIndexes(createForm.body).map((placeholder, index) => (
                                            <div key={placeholder}>
                                                <label className="block text-[11px] font-semibold text-slate-500 mb-2">
                                                    {`{{${placeholder}}}`} example *
                                                </label>
                                                <input
                                                    value={createForm.bodyExamples.split("\n")[index] || ""}
                                                    onChange={(event) => updateCreateBodyExample(index, event.target.value)}
                                                    placeholder={`Sample value for {{${placeholder}}}`}
                                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-dashed border-slate-800 bg-slate-950/60 px-3 py-4 text-xs text-slate-500">
                                        Add variables like {`{{1}}`} in the body to enter example values.
                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-slate-300">Buttons</p>
                                        <p className="text-[11px] text-slate-500 mt-1">Add quick reply, URL, or phone number buttons.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addCreateButton}
                                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        Add Button
                                    </button>
                                </div>

                                {createForm.buttons.length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-slate-700 bg-slate-950/70 px-4 py-4 text-xs font-semibold text-slate-500">
                                        No buttons added
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {createForm.buttons.map((button, index) => (
                                            <div key={`${button.type}-${index}`} className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
                                                <div className="grid grid-cols-1 md:grid-cols-[150px_1fr_auto] gap-3 items-end">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Type</label>
                                                        <select
                                                            value={button.type}
                                                            onChange={(event) => updateCreateButton(index, "type", event.target.value)}
                                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                        >
                                                            <option value="QUICK_REPLY">Quick Reply</option>
                                                            <option value="URL">URL</option>
                                                            <option value="PHONE_NUMBER">Phone</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Button Text</label>
                                                        <input
                                                            value={button.text}
                                                            onChange={(event) => updateCreateButton(index, "text", event.target.value)}
                                                            placeholder="Button label"
                                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCreateButton(index)}
                                                        className="h-[42px] px-3 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 transition-colors"
                                                        title="Remove button"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                {button.type === "URL" && (
                                                    <input
                                                        value={button.url}
                                                        onChange={(event) => updateCreateButton(index, "url", event.target.value)}
                                                        placeholder="https://smartmatrimony.ai"
                                                        className="mt-3 w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    />
                                                )}
                                                {button.type === "PHONE_NUMBER" && (
                                                    <input
                                                        value={button.phone_number}
                                                        onChange={(event) => updateCreateButton(index, "phone_number", event.target.value)}
                                                        placeholder="+919999999999"
                                                        className="mt-3 w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 bg-slate-900/95 shrink-0">
                            <button
                                type="button"
                                onClick={closeCreateModal}
                                className="px-4 py-2.5 text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitCreateTemplate}
                                disabled={createLoading}
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:hover:bg-emerald-600 text-white rounded-lg transition-colors shadow-lg shadow-emerald-950/30"
                            >
                                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Submit to Meta
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedTemplate && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl flex flex-col">
                        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800 bg-slate-900/95 shrink-0">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
                                    Send Template
                                </p>
                                <h3 className="text-xl font-bold text-white break-words">
                                    Send {selectedTemplate.template_name}
                                </h3>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                                    <span className="font-mono text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-0.5">{selectedTemplateKey}</span>
                                    <span>{selectedTemplate.language_code}</span>
                                    <span className={isTemplateAutomatic(selectedTemplate, selectedTemplateKey) ? "text-emerald-300" : "text-amber-300"}>
                                        {isTemplateAutomatic(selectedTemplate, selectedTemplateKey) ? "Automatic" : "Manual"}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeSendModal}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6">
                                <div className="space-y-5">
                                    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                                <label className="block text-xs font-semibold text-slate-400 mb-3">Recipients</label>
                                <div className="grid grid-cols-3 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                    {([
                                        ["phone", "Single Phone"],
                                        ["search", "Search Users"],
                                        ["all", "All Users"],
                                    ] as Array<[RecipientMode, string]>).map(([value, label]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => setRecipientMode(value)}
                                            className={`px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                recipientMode === value
                                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-950/40"
                                                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <label className="block text-xs font-semibold text-slate-400">Template Language</label>
                                    <span className="text-[11px] text-slate-500">
                                        Hindi requires an approved Hindi Meta template
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 rounded-lg border border-slate-800 bg-slate-950 p-1 max-w-md">
                                    {templateLanguageOptions.map(([value, label]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => setTemplateLanguage(value)}
                                            className={`px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                templateLanguage === value
                                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-950/40"
                                                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                {templateLanguage === "hi" && !selectedTemplate.language_code?.toLowerCase().startsWith("hi") && (
                                    <p className="text-[11px] text-amber-300">
                                        The preview may still show the Hinglish catalog text. WhatsApp will send the approved Hindi Meta variant if it exists for this template key.
                                    </p>
                                )}
                            </div>

                            {recipientMode !== "phone" && (
                                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <label className="block text-xs font-semibold text-slate-400">Recipient Filters</label>
                                        <span className="text-[11px] text-slate-500">
                                            {recipientCountLoading
                                                ? "Counting matching users..."
                                                : recipientCount !== null
                                                  ? `${recipientCount} matching recipients`
                                                  : "Applied to selected, search, and all-user sends"}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-2">Gender</label>
                                            <div className="grid grid-cols-3 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                {recipientFilterGroups.gender.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => setGenderFilter(value)}
                                                        className={`px-2 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                            genderFilter === value
                                                                ? "bg-blue-600 text-white"
                                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-2">Membership</label>
                                            <div className="grid grid-cols-3 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                {recipientFilterGroups.premium.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => setPremiumFilter(value)}
                                                        className={`px-2 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                            premiumFilter === value
                                                                ? "bg-blue-600 text-white"
                                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-2">Community</label>
                                            <div className="grid grid-cols-3 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                {recipientFilterGroups.community.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => setCommunityFilter(value)}
                                                        className={`px-2 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                            communityFilter === value
                                                                ? "bg-blue-600 text-white"
                                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-2">Account Status</label>
                                            <div className="grid grid-cols-4 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                {recipientFilterGroups.status.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => {
                                                            setUserStatusFilter(value);
                                                            setActiveOnly(value === "active");
                                                        }}
                                                        className={`px-2 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                            userStatusFilter === value
                                                                ? "bg-blue-600 text-white"
                                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-2">Profile Photo</label>
                                            <div className="grid grid-cols-3 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                {recipientFilterGroups.photo.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => setPhotoFilter(value)}
                                                        className={`px-2 py-2 text-xs font-semibold rounded-md transition-colors ${
                                                            photoFilter === value
                                                                ? "bg-blue-600 text-white"
                                                                : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            {photoFilter === "missing_photo" && (
                                                <p className="mt-2 text-[11px] text-amber-300">
                                                    Sends only to users with no active profile photo in the photos table.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-4 space-y-3">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <div>
                                                <div className="text-xs font-semibold text-slate-300">Advanced Filters</div>
                                                <div className="text-[11px] text-slate-500 mt-1">
                                                    Add custom table and column rules for exact recipient targeting.
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <div className="grid grid-cols-2 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                    {([
                                                        ["all", "Match All"],
                                                        ["any", "Match Any"],
                                                    ] as Array<[RecipientCustomFilterMatch, string]>).map(([value, label]) => (
                                                        <button
                                                            key={value}
                                                            type="button"
                                                            onClick={() => setRecipientCustomFilterMatch(value)}
                                                            className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                                                                recipientCustomFilterMatch === value
                                                                    ? "bg-blue-600 text-white"
                                                                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                            }`}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setRecipientCustomFilters((prev) => [...prev, makeRecipientCustomFilter()])}
                                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-white transition-colors"
                                                >
                                                    <Plus className="w-3.5 h-3.5" />
                                                    Add Filter
                                                </button>
                                            </div>
                                        </div>

                                        {recipientCustomFilters.length === 0 ? (
                                            <div className="rounded-lg border border-dashed border-slate-800 bg-slate-950/60 px-3 py-4 text-xs text-slate-500">
                                                No advanced filters added.
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {recipientCustomFilters.map((filter, index) => (
                                                    <div key={filter.id} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-3">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                                Filter {index + 1}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeRecipientCustomFilter(filter.id)}
                                                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition-colors"
                                                                aria-label="Remove filter"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-[170px_1fr_190px] gap-3">
                                                            <select
                                                                value={filter.table}
                                                                onChange={(event) => updateRecipientCustomFilter(filter.id, { table: event.target.value })}
                                                                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                            >
                                                                <option value="">Select table</option>
                                                                {templateSourceTables.map((table) => (
                                                                    <option key={table} value={table}>{table}</option>
                                                                ))}
                                                            </select>
                                                            <select
                                                                value={filter.source}
                                                                onChange={(event) => updateRecipientCustomFilter(filter.id, { source: event.target.value })}
                                                                disabled={!filter.table}
                                                                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                            >
                                                                <option value="">Select column</option>
                                                                {getSourcesForTable(filter.table).map((source) => (
                                                                    <option key={source.value} value={source.value}>
                                                                        {source.column || source.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <select
                                                                value={filter.operator}
                                                                onChange={(event) => updateRecipientCustomFilter(filter.id, { operator: event.target.value as RecipientCustomFilterOperator })}
                                                                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                            >
                                                                {recipientCustomFilterOperators.map(([value, label]) => (
                                                                    <option key={value} value={value}>{label}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {operatorNeedsRightColumn(filter.operator) ? (
                                                            <div className="grid grid-cols-1 md:grid-cols-[170px_1fr] gap-3">
                                                                <select
                                                                    value={filter.rightTable}
                                                                    onChange={(event) => updateRecipientCustomFilter(filter.id, { rightTable: event.target.value })}
                                                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                                >
                                                                    <option value="">Compare table</option>
                                                                    {templateSourceTables.map((table) => (
                                                                        <option key={table} value={table}>{table}</option>
                                                                    ))}
                                                                </select>
                                                                <select
                                                                    value={filter.rightSource}
                                                                    onChange={(event) => updateRecipientCustomFilter(filter.id, { rightSource: event.target.value })}
                                                                    disabled={!filter.rightTable}
                                                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                                >
                                                                    <option value="">Compare column</option>
                                                                    {getSourcesForTable(filter.rightTable).map((source) => (
                                                                        <option key={source.value} value={source.value}>
                                                                            {source.column || source.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        ) : operatorNeedsValue(filter.operator) ? (
                                                            <div className={`grid grid-cols-1 ${filter.operator === "between" ? "md:grid-cols-2" : ""} gap-3`}>
                                                                <input
                                                                    value={filter.value}
                                                                    onChange={(event) => updateRecipientCustomFilter(filter.id, { value: event.target.value })}
                                                                    placeholder={filter.operator === "in" || filter.operator === "not_in" ? "Comma separated values" : "Value"}
                                                                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                                />
                                                                {filter.operator === "between" && (
                                                                    <input
                                                                        value={filter.valueTo}
                                                                        onChange={(event) => updateRecipientCustomFilter(filter.id, { valueTo: event.target.value })}
                                                                        placeholder="To value"
                                                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                                    />
                                                                )}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {recipientMode === "phone" && (
                                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Recipient Name</label>
                                        <input
                                            value={recipientName}
                                            onChange={(event) => setRecipientName(event.target.value)}
                                            placeholder="Name"
                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Phone Number</label>
                                        <input
                                            value={phoneNumber}
                                            onChange={(event) => handlePhoneChange(event.target.value)}
                                            placeholder="10 digit number"
                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                        />
                                    </div>
                                </div>
                            )}

                            {recipientMode === "search" && (
                                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            value={userSearchQuery}
                                            onChange={(event) => handleUserSearchChange(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    runUserSearch(userSearchQuery);
                                                }
                                            }}
                                            placeholder="Search by name, user id, phone, email, city..."
                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => runUserSearch(userSearchQuery)}
                                            disabled={searchLoading || !userSearchQuery.trim()}
                                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-semibold transition-colors shrink-0"
                                        >
                                            {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                            Search
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        Select specific users below, or send to all users matching the search query.
                                    </p>
                                    {searchResults.length > 0 && (
                                        <div className="border border-slate-800 rounded-lg overflow-hidden">
                                            <div className="flex items-center justify-between px-3 py-2 bg-slate-950 border-b border-slate-800">
                                                <span className="hidden">
                                                    {searchResults.length} found · {selectedUserIds.length} total selected
                                                </span>
                                                <span className="text-xs text-slate-400">
                                                    {searchResults.length} found - {selectedUserIds.length} total selected
                                                </span>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={toggleSelectAllSearchResults}
                                                        className="text-xs text-blue-300 hover:text-blue-200"
                                                    >
                                                        {searchResults.every((u) => selectedUserIds.includes(u.id)) ? "Deselect all" : "Select all from results"}
                                                    </button>
                                                    {selectedUserIds.length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedUserIds([])}
                                                            className="text-xs text-red-300 hover:text-red-200"
                                                        >
                                                            Clear all
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="max-h-56 overflow-y-auto divide-y divide-slate-800">
                                                {searchResults.map((user) => (
                                                    <label
                                                        key={user.id}
                                                        className="flex items-start gap-3 px-3 py-2 hover:bg-slate-950/70 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUserIds.includes(user.id)}
                                                            onChange={() => {
                                                                toggleUserSelection(user.id, user);
                                                                setUserSearchQuery("");
                                                                setSearchResults([]);
                                                            }}
                                                            className="mt-1"
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="text-sm text-white">{user.name}</div>
                                                            <div className="text-[11px] text-slate-400 font-mono">{user.id}</div>
                                                            <div className="hidden">
                                                                {user.phone || "Phone hidden"} · {user.status || "Unknown"} · {user.location || "N/A"}
                                                            </div>
                                                            <div className="text-[11px] text-slate-500">
                                                                {user.phone || "Phone hidden"} - {user.status || "Unknown"} - {user.location || "N/A"}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedUserIds.length > 0 && (
                                        <div className="border border-emerald-500/30 bg-emerald-500/10 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-xs font-semibold text-emerald-300">All Selected Users ({selectedUserIds.length})</label>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedUserIds([]);
                                                        setSelectedUsersMap({});
                                                    }}
                                                    className="text-xs text-red-300 hover:text-red-200"
                                                >
                                                    Clear all
                                                </button>
                                            </div>
                                            <div className="max-h-48 overflow-y-auto space-y-1">
                                                {selectedUserIds.map((userId) => {
                                                    const user = selectedUsersMap[userId];
                                                    return (
                                                        <div
                                                            key={userId}
                                                            className="flex items-start justify-between gap-2 px-2 py-1.5 bg-emerald-500/20 rounded border border-emerald-500/30 text-xs"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="text-emerald-100">{user?.name || userId}</div>
                                                                <div className="text-emerald-300/60 font-mono text-[10px]">{userId}</div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    toggleUserSelection(userId);
                                                                    setSelectedUsersMap((m) => {
                                                                        const updated = { ...m };
                                                                        delete updated[userId];
                                                                        return updated;
                                                                    });
                                                                }}
                                                                className="text-red-300 hover:text-red-200 mt-0.5 shrink-0"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {recipientMode === "all" && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
                                    <div className="flex items-start gap-2 text-amber-200">
                                        <Users className="w-4 h-4 mt-0.5 shrink-0" />
                                        <p className="text-sm">
                                            This will send the template to users matching the filters above{recipientCount !== null ? ` (${recipientCount} recipients)` : ""}.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-400 mb-2">Max recipients</label>
                                            <input
                                                type="number"
                                                min={0}
                                                max={10000}
                                                value={sendLimit}
                                                onChange={(event) => setSendLimit(Math.max(0, Number(event.target.value) || 0))}
                                                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                            />
                                        </div>
                                        <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-2.5">
                                            <div className="text-[11px] font-semibold text-slate-500 mb-1">Current status filter</div>
                                            <div className="text-sm font-semibold text-slate-200 capitalize">
                                                {userStatusFilter.replace("_", " ")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {recipientMode === "search" && (
                                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Send limit</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={10000}
                                            value={sendLimit}
                                            onChange={(event) => setSendLimit(Math.max(0, Number(event.target.value) || 0))}
                                            className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                        />
                                    </div>
                                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-2.5">
                                        <div className="text-[11px] font-semibold text-slate-500 mb-1">Current status filter</div>
                                        <div className="text-sm font-semibold text-slate-200 capitalize">
                                            {userStatusFilter.replace("_", " ")}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedTemplate.parameters?.length > 0 && (
                                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <label className="block text-xs font-semibold text-slate-400">Template Parameters</label>
                                        <span className="text-[11px] text-slate-500">Manual or per-user field values</span>
                                    </div>
                                    {selectedTemplate.parameters.map((parameter) => (
                                        <div key={parameter.key} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-3">
                                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                                <label className="block text-xs font-semibold text-slate-300">
                                                    {parameter.label || parameter.key} {parameter.required ? "*" : ""}
                                                </label>
                                                <div className="grid grid-cols-2 rounded-lg border border-slate-800 bg-slate-950 p-1 md:w-56">
                                                    {([
                                                        ["manual", "Manual"],
                                                        ["field", "User Field"],
                                                    ] as Array<[TemplateValueMode, string]>).map(([mode, label]) => (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            onClick={() => updateTemplateValueMapping(parameter.key, { mode })}
                                                            className={`px-2 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                                                                (templateValueMappings[parameter.key]?.mode || "manual") === mode
                                                                    ? "bg-blue-600 text-white"
                                                                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                                                            }`}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {(templateValueMappings[parameter.key]?.mode || "manual") === "field" ? (
                                                <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_180px] gap-3">
                                                    <select
                                                        value={templateValueMappings[parameter.key]?.table || ""}
                                                        onChange={(event) =>
                                                            updateTemplateValueMapping(parameter.key, {
                                                                mode: "field",
                                                                table: event.target.value,
                                                                source: "",
                                                            })
                                                        }
                                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    >
                                                        <option value="">Select table</option>
                                                        {templateSourceTables.map((table) => (
                                                            <option key={table} value={table}>{table}</option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        value={templateValueMappings[parameter.key]?.source || ""}
                                                        onChange={(event) => updateTemplateValueMapping(parameter.key, { mode: "field", source: event.target.value })}
                                                        disabled={!templateValueMappings[parameter.key]?.table}
                                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    >
                                                        <option value="">Select column</option>
                                                        {templateFieldSources
                                                            .filter((source) => (source.table || source.value.split(".")[0]) === templateValueMappings[parameter.key]?.table)
                                                            .map((source) => (
                                                            <option key={source.value} value={source.value}>{source.column || source.label}</option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        value={templateValueMappings[parameter.key]?.default || ""}
                                                        onChange={(event) => updateTemplateValueMapping(parameter.key, { default: event.target.value })}
                                                        placeholder="Fallback"
                                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {parameter.key === "header_image_url" && (
                                                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-bold text-blue-200 hover:bg-blue-500/20 transition-colors">
                                                            {uploadingTemplateParamKey === parameter.key ? "Uploading..." : "Select Image"}
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpeg,image/webp,image/gif"
                                                                disabled={uploadingTemplateParamKey === parameter.key}
                                                                onChange={(event) => {
                                                                    const file = event.target.files?.[0];
                                                                    handleTemplateHeaderImageSelect(parameter.key, file);
                                                                    event.currentTarget.value = "";
                                                                }}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                    )}
                                                    <input
                                                        value={templateParams[parameter.key] || ""}
                                                        onChange={(event) => {
                                                            const value = event.target.value;
                                                            setTemplateParams((prev) => ({
                                                                ...prev,
                                                                [parameter.key]: value,
                                                            }));
                                                            updateTemplateValueMapping(parameter.key, { mode: "manual", value });
                                                        }}
                                                        placeholder={parameter.default || parameter.key}
                                                        className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <p className="text-[11px] text-slate-500">
                                        Use User Field for bulk sends when every recipient needs a different value.
                                    </p>
                                </div>
                            )}

                                </div>

                                <aside className="lg:sticky lg:top-0 h-fit rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <label className="block text-xs font-semibold text-slate-400">Full Message Preview</label>
                                        <button
                                            type="button"
                                            onClick={() => copyTemplate(buildTemplatePreview(selectedTemplate, sendPreviewParams))}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 rounded-md transition-colors"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                            Copy
                                        </button>
                                    </div>
                                    <pre className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-200 bg-slate-900/70 border border-slate-800 rounded-lg p-4 font-sans max-h-[52vh] overflow-y-auto">
                                        {buildTemplatePreview(selectedTemplate, sendPreviewParams)}
                                    </pre>
                                    {sendSummary && (
                                        <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
                                            {sendSummary}
                                        </div>
                                    )}
                                    {sendResults.length > 0 && (
                                        <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/70 overflow-hidden">
                                            <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-3 py-2">
                                                <span className="text-xs font-semibold text-slate-300">Recipient Logs</span>
                                                <span className="text-[11px] text-slate-500">
                                                    {sendResults.filter((item) => item.success).length} sent / {sendResults.length} total
                                                </span>
                                            </div>
                                            <div className="max-h-72 overflow-y-auto custom-scrollbar divide-y divide-slate-800">
                                                {sendResults.map((item, index) => {
                                                    const errorMessage =
                                                        item.error ||
                                                        item.response?.error?.message ||
                                                        item.response?.message ||
                                                        (item.skipped ? "Skipped" : item.success ? "Sent" : "Failed");
                                                    return (
                                                        <div key={`${item.user_id || item.phone || index}-${index}`} className="px-3 py-2">
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <div className="truncate text-sm font-semibold text-slate-200">
                                                                        {item.name || "Unknown"}
                                                                    </div>
                                                                    <div className="mt-0.5 truncate text-[11px] text-slate-500">
                                                                        {item.user_id || "No user id"} - {item.phone || "No phone"}
                                                                    </div>
                                                                </div>
                                                                <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${
                                                                    item.success
                                                                        ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                                                                        : item.skipped
                                                                          ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                                                          : "bg-rose-500/10 text-rose-300 border border-rose-500/30"
                                                                }`}>
                                                                    {item.success ? "SENT" : item.skipped ? "SKIPPED" : "FAILED"}
                                                                </span>
                                                            </div>
                                                            {!item.success && (
                                                                <div className="mt-1 text-[11px] leading-4 text-slate-400 break-words">
                                                                    {errorMessage}
                                                                </div>
                                                            )}
                                                            {item.status_code ? (
                                                                <div className="mt-1 text-[10px] text-slate-600">Status: {item.status_code}</div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </aside>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 bg-slate-900/95 shrink-0">
                            <button
                                type="button"
                                onClick={closeSendModal}
                                className="px-4 py-2.5 text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={sendSelectedTemplate}
                                disabled={sendLoading}
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600 text-white rounded-lg transition-colors shadow-lg shadow-blue-950/30"
                            >
                                {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {recipientMode === "all"
                                    ? "Send to All"
                                    : recipientMode === "search" && selectedUserIds.length === 0
                                      ? "Send to Search Results"
                                      : "Send Template"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateCatalogView;

